<template>
  <div>
    <!-- Script inicial -->
    <div v-if="!isPresencial" class="script-box">
      <p class="script-label">
        <i class="icofont-microphone"></i> Script para o Facilitador:
      </p>
      <p class="script-text">"{{ step.script }}"</p>
    </div>
    <div v-else class="presencial-box">
      <p class="presencial-text">
        <i class="icofont-check-circled"></i> {{ step.script }}
      </p>
    </div>

    <!-- Tabs -->
    <div class="oficina-tabs">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="oficina-tab"
        :class="{ active: activeTab === tab.id }"
        @click="updateTab(tab.id)"
      >
        <i :class="getIconClass(tab.icon)"></i> {{ tab.label }}
      </button>
    </div>

    <div class="oficina-tab-content">
      <!-- Tab Projeção (Presencial) ou Transmissão (Remoto) -->
      <div v-if="activeTab === 'projecao' || activeTab === 'transmissao'">
        <h3 class="tech-setup-title">{{ currentTab.sectionTitle }}</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item" v-for="(item, idx) in currentTab.items" :key="idx">
            <strong class="tech-setup-card-title">{{ item.title }}</strong>
            <p class="tech-setup-card-text">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <!-- Tab Áudio Local (Presencial) ou Som da Sala (Remoto) -->
      <div v-else-if="activeTab === 'audio-local' || activeTab === 'som-sala'">
        <h3 class="tech-setup-title">{{ currentTab.sectionTitle }}</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item" v-for="(item, idx) in currentTab.items" :key="idx">
            <strong class="tech-setup-card-title">{{ item.title }}</strong>
            <p class="tech-setup-card-text">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <!-- Tab Internet (Apenas Remoto) -->
      <div v-else-if="activeTab === 'internet'">
        <h3 class="tech-setup-title">{{ currentTab.sectionTitle }}</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item" v-for="(item, idx) in currentTab.items" :key="idx">
            <strong class="tech-setup-card-title">{{ item.title }}</strong>
            <p class="tech-setup-card-text">{{ item.description }}</p>
            <p v-if="item.tip" class="tech-setup-tip">
              <i class="icofont-info-circle"></i> {{ item.tip }}
            </p>
          </div>
        </div>
      </div>

      <!-- Tab Acesso Remoto (Apenas Remoto) -->
      <div v-else-if="activeTab === 'acesso-remoto-sala'">
        <div class="remote-access-box">
          <p class="remote-access-text">
            Se precisarmos assumir o controle do computador da sala:
          </p>
        </div>
        <h3 class="tech-setup-title">{{ currentTab.sectionTitle }}</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item">
            <div class="remote-access-steps">
              <div class="remote-access-step">
                <span class="step-number">1</span>
                <p class="step-text">Abra o link SOS que te mandei no WhatsApp.</p>
              </div>
              <div class="remote-access-step">
                <span class="step-number">2</span>
                <p class="step-text">Clique em 'Gerar Código' (ou 'Receber Suporte').</p>
              </div>
              <div class="remote-access-step">
                <span class="step-number">3</span>
                <p class="step-text"><strong>Me envie uma foto ou o número do código pelo WhatsApp agora.</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="step.tip" class="oficina-slide-tip">
      <i class="icofont-info-circle"></i> {{ step.tip }}
    </div>
  </div>
</template>

<script>
import { copyToClipboard } from '../../utils'

export default {
  name: 'RemoteSetupSlide',
  props: {
    step: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    activeTabs: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: '',
    }
  },
  computed: {
    isPresencial() {
      return this.config.platform === 'Presencial'
    },
    visibleTabs() {
      if (!this.step.tabs) return []
      return this.step.tabs
    },
    currentTab() {
      return this.step.tabs?.find(tab => tab.id === this.activeTab) || {}
    },
  },
  mounted() {
    // Restaurar tab ativa se houver, senão usar a primeira tab visível
    if (this.activeTabs.remoteSetup) {
      this.activeTab = this.activeTabs.remoteSetup
    } else if (this.visibleTabs.length > 0) {
      this.activeTab = this.visibleTabs[0].id
    }
  },
  methods: {
    updateTab(tabId) {
      this.activeTab = tabId
      this.$emit('update-tab', 'remoteSetup', tabId)
    },
    getIconClass(icon) {
      const iconMap = {
        Monitor: 'icofont-monitor',
        Volume: 'icofont-volume-up',
        Cast: 'icofont-presentation',
        WiFi: 'icofont-wifi',
      }
      return iconMap[icon] || 'icofont-settings'
    },
    async copySosLink() {
      if (!this.currentTab.sosLink) return
      const ok = await copyToClipboard(this.currentTab.sosLink)
      if (ok && this.$toast) {
        this.$toast.success('Link SOS copiado!', 'Copiado', {
          position: 'topRight',
        })
      }
    },
  },
  emits: ['update-tab'],
}
</script>

<style scoped>
/* Script Box (Remoto) */
.script-box {
  background: #eff6ff;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #bfdbfe;
}

.script-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.script-text {
  color: #1e40af;
  font-size: 16px;
  font-style: italic;
  margin: 0;
}

/* Presencial Box */
.presencial-box {
  background: #dcfce7;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #86efac;
}

.presencial-text {
  color: #166534;
  font-size: 16px;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

/* Tabs */
.oficina-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.oficina-tabs::-webkit-scrollbar {
  height: 6px;
}

.oficina-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.oficina-tabs::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.oficina-tabs::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.oficina-tab {
  padding: 12px 20px;
  border: 2px solid #e2e8f0;
  border-right: none;
  background: white;
  border-radius: 0;
  cursor: pointer;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.oficina-tab:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.oficina-tab:last-child {
  border-right: 2px solid #e2e8f0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.oficina-tab.active {
  background: #0FAFD7;
  border-color: #0FAFD7;
  color: white;
  z-index: 1;
  position: relative;
}

.oficina-tab.active + .oficina-tab {
  border-left-color: #0FAFD7;
}

.oficina-tab-content {
  min-height: 300px;
}

.tech-setup-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
}

.tech-setup-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tech-setup-card-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 20px;
}

.tech-setup-card-title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.tech-setup-card-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

.tech-setup-tip {
  font-size: 14px;
  color: #f59e0b;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

/* Remote Access Box (Roxo) */
.remote-access-box {
  background: #f3e8ff;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #d8b4fe;
}

.remote-access-text {
  color: #7c3aed;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

/* Remote Access Steps */
.remote-access-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.remote-access-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}

.step-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  padding-top: 4px;
}

.oficina-slide-tip {
  background: #eff6ff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  margin-top: 24px;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
</style>
